import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { appTableFiltersInstance } from "pages/TrackingDiagnostics/TrackingDiagnosticsTableColumns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { SingleSorterType } from "services/AppTableFilters";
import { diagnosticsPackagesTableColumns } from "./DiagnosticsPackagesTableColumns";
import { IDiagnosticsPackages } from "types/models/DiagnosticsPackages";
import { TableCell } from "@trackman/web-shared-components/components/Table/TableCell";
import { AppSectionErrorBoundary } from "components/ui/AppSectionErrorBoundary";
import AppPageHeader from "components/ui/AppPageHeader";
import { StaticTableWithFilters } from "components/Tables/StaticTableWithFilters";
import AppCopyText from "components/ui/AppCopyText";
import { AppButton } from "components/form";
import { FormDiagnosticsPackageDiaglog } from "./components/dialogs/FormDiagnosticsPackageDiaglog";
import FormDiagnosticsPackageResponseModal from "./components/FormDiagnosticsPackageResponseModal";
import AppConfirmDialog from "components/ui/AppConfirmDialog";
import AppUseIcon from "components/ui/AppUseIcon";

export const DiagnosticsPackagesPage = observer(() => {
    const { t } = useTranslation();

    const history = useHistory();
    const location = useLocation();

    const { diagnosticsPackagesStore, trackingDiagnosticsStore } = useStores();
    const [isManageDialogOpened, setIsManageDialogOpened] = useState<boolean>(false);

    const [diagnosticsPackageToRemove, setDiagnosticsPackageToRemove] = useState<IDiagnosticsPackages | null>(null);

    const autoSortColumn = "addedAtUtc";
    const DefaultAutoSortColumn: SingleSorterType = {
        id: autoSortColumn,
        type: "date",
        field: autoSortColumn,
        value: 'desc',
        active: true
    };

    useEffect(() => {
        async function fetchData() {
            await Promise.all([
                diagnosticsPackagesStore.getDiagnosticsPackages(),
                trackingDiagnosticsStore.getTrackingDiagnostics()
            ]);
        };
        fetchData();

        appTableFiltersInstance.clearSorters();
        appTableFiltersInstance.addSorter(DefaultAutoSortColumn);
    }, [diagnosticsPackagesStore, trackingDiagnosticsStore, diagnosticsPackagesStore.page, diagnosticsPackagesStore.take]);

    //Act based on SearchParams
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const selectedDiagnosticsPackageFromUrl = searchParams.get('selectedDiagnosticsPackage');

        if (selectedDiagnosticsPackageFromUrl && !isManageDialogOpened) {
            const diagnosticsPackage = diagnosticsPackagesStore.diagnosticsPackages.find(x => x.id == selectedDiagnosticsPackageFromUrl);
            if (diagnosticsPackage) {
                handleSelectingDiagnosticPackage(diagnosticsPackage)
            }
        }

    }, [diagnosticsPackagesStore.diagnosticsPackages]);

    useEffect(() => {
        if (!isManageDialogOpened && diagnosticsPackagesStore.currentSelectedDiagnosticsPackages) {
            diagnosticsPackagesStore.setCurrentSelectedDiagnosticsPackage(null);
        }
    }, [isManageDialogOpened]);

    const rows = useMemo(() => diagnosticsPackagesStore.diagnosticsPackages, [diagnosticsPackagesStore.diagnosticsPackages]);

    const columns = useMemo(() => diagnosticsPackagesTableColumns(), []);

    const renderCell = useCallback(
        ({ cellValue, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {

            if (columnId === 'diagnosticsPackageId') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                        <AppCopyText className='ml-1 ' text={cellValue} />
                    </div>
                );
            }

            if (columnId === 'addedAtUtc' || columnId === 'diagnosticsName' || columnId === 'diagnosticsPackageDescription') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                    </div>
                );
            }

            if (columnId === 'actions') {
                const rowData: IDiagnosticsPackages = row as IDiagnosticsPackages;
                return (
                    <div className='d-flex align-items-center w-100'>
                        <AppButton
                            variant='secondary'
                            className='border-secondary'
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleSelectingDiagnosticPackage(rowData);
                            }}
                        >
                            {t('DiagnosticsPackages.Action.Manage')}
                        </AppButton>
                        <AppButton
                            variant='secondary'
                            className='border-secondary'
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleRemoveWarning(row);
                            }}
                            disabled={diagnosticsPackagesStore.isRemovingDiagnosticsPackage}
                        >
                            {t('remove')}
                            <AppUseIcon name='minusWithRoundedBorder' width='16' height='16' className='ml-2' color='#A0A0A0' />
                        </AppButton>
                    </div>
                );
            }

            return cellValue || '-';
        },
        []
    );

    const handleSelectingDiagnosticPackage = useCallback((diagnosticsPackage: IDiagnosticsPackages) => {
        diagnosticsPackagesStore.setCurrentSelectedDiagnosticsPackage(diagnosticsPackage);
        setIsManageDialogOpened(true);
        updateSelectedDiagnosticsPackageUrlParams(diagnosticsPackage, true);
    }, []);

    const handleClosingManageDialog = useCallback(() => {
        setIsManageDialogOpened(false);
        updateSelectedDiagnosticsPackageUrlParams(null, false);
    }, []);

    const handleRemovingDiagnosticsPackage = useCallback(() => {
        if (!diagnosticsPackageToRemove) return;
        diagnosticsPackagesStore.removeDiagnosticsPackage(diagnosticsPackageToRemove.id, () => setDiagnosticsPackageToRemove(null));
    }, [diagnosticsPackageToRemove]);

    const handleRemoveWarning = useCallback(setDiagnosticsPackageToRemove, []);

    const handleCancelRemoveDiagnosticsPackage = useCallback(() => setDiagnosticsPackageToRemove(null), []);

    const updateSelectedDiagnosticsPackageUrlParams = useCallback((diagnosticPackage: IDiagnosticsPackages | null, isApplyingParam: boolean) => {
        const searchParams = new URLSearchParams(location.search);
        if (isApplyingParam && diagnosticPackage) {
            searchParams.delete('selectedDiagnosticsPackage');
            searchParams.append('selectedDiagnosticsPackage', diagnosticPackage.id);
        }
        else {
            searchParams.delete('selectedDiagnosticsPackage');
        }

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });
    }, []);

    const rowMapper = useCallback((cells: any[]) => {
        return (
            <>
                {cells.map(({ getCellProps, column, row, render, value }) => (
                    <TableCell {...getCellProps()} align={column.align} onClick={(e) => {
                        e?.stopPropagation();
                        handleSelectingDiagnosticPackage(row.original as IDiagnosticsPackages);
                    }}>
                        {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
                    </TableCell>
                ))}
            </>
        );
    }, []);

    return (
        <AppSectionErrorBoundary>
            <div className='diagnostics-packages-tables'>
                <AppPageHeader
                    title={`${t('DiagnosticsPackages.Title')}`}
                    backBtn={{ isVisible: true, onClick: () => history.push('/') }}
                >
                </AppPageHeader>

                <StaticTableWithFilters<IDiagnosticsPackages>
                    appTableFiltersInstance={appTableFiltersInstance}
                    rowMapper={rowMapper}
                    columns={columns}
                    data={rows}
                    isLoading={diagnosticsPackagesStore.isLoadingDiagnosticsPackages && trackingDiagnosticsStore.isLoadingTrackingDiagnostics}
                />

                <FormDiagnosticsPackageDiaglog
                    isOpened={isManageDialogOpened && !diagnosticsPackagesStore.isLoadingDiagnosticsPackages && !trackingDiagnosticsStore.isLoadingTrackingDiagnostics}
                    onClose={handleClosingManageDialog}
                    onSuccessAfterClose={() => { }}
                    diagnosticsPackage={diagnosticsPackagesStore.currentSelectedDiagnosticsPackages}
                />

                <AppConfirmDialog
                    isDialogOpen={diagnosticsPackageToRemove !== null}
                    isLoading={diagnosticsPackagesStore.isRemovingDiagnosticsPackage}
                    onConfirm={handleRemovingDiagnosticsPackage}
                    onCancel={handleCancelRemoveDiagnosticsPackage}
                    title={`${t('DiagnosticsPackages.Dialog.Title.RemoveDiagnosticsPackage')}`}
                    message={`${t('DiagnosticsPackages.Dialog.Message')}${diagnosticsPackageToRemove?.name ? ' (' + diagnosticsPackageToRemove?.name + ')' : ''}?`}
                />
            </div>
            <FormDiagnosticsPackageResponseModal
                diagnositcsPackageUrl={diagnosticsPackagesStore.currentFormResponseUrl ?? '-'}
            />
        </AppSectionErrorBoundary>
    );
});

export default DiagnosticsPackagesPage;